import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { OrganizationDTO, UserDTO } from '@keystone/shared';
import { api, endpoints, type AuthSession } from '../lib/api.js';
import { asProblem, type ApiProblem } from '../lib/problem.js';
import type { ActionResult } from '../lib/hooks.js';

/**
 * Session state.
 *
 * ## What this provider does and does not own
 *
 * It owns the *identity* of the caller: who they are, which organisations they
 * belong to, and whether the session is still alive. It does **not** own the
 * access token — that lives inside the `ApiClient` and never enters React, which
 * is what keeps it out of reach of a devtools extension and out of reach of any
 * component that might accidentally render it.
 *
 * ## The silent restore
 *
 * On mount the provider calls `api.refresh()`. That sends the `HttpOnly` refresh
 * cookie, and if it is still valid the browser gets a new access token with no
 * user interaction. This is what makes a page reload feel like a reload rather
 * than a sign-out, while the access token itself remains memory-only. If the
 * cookie is absent or dead the call fails and the caller is anonymous — which is
 * also the correct outcome for a first-time visitor, so the two cases do not need
 * to be distinguished.
 *
 * ## Why `status` is a three-state enum
 *
 * `loading` is not the same as `anonymous`. Collapsing them makes the router
 * redirect to `/login` for one frame on every reload, which is both ugly and a
 * genuine bug: a user who is signed in and refreshes the page is thrown at the
 * login form and then bounced back.
 */

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: UserDTO | null;
  organizations: OrganizationDTO[];
  /** The organisation matching `slug`, or null if the caller is not a member. */
  organizationFor: (slug: string | undefined) => OrganizationDTO | null;
  signIn: (email: string, password: string) => Promise<ActionResult<void>>;
  signUp: (input: { email: string; password: string; name: string }) => Promise<ActionResult<void>>;
  signOut: () => Promise<void>;
  signOutEverywhere: () => Promise<ActionResult<number>>;
  reloadOrganizations: () => Promise<void>;
  setUser: (user: UserDTO) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUserState] = useState<UserDTO | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationDTO[]>([]);

  /**
   * Clears everything, without touching the server.
   *
   * Registered as the client's `onAuthLost` callback, so it runs when a refresh
   * is definitively rejected — a revoked family, an expired cookie, a disabled
   * account. The client calls it exactly once per failed refresh, and because
   * refreshes are single-flight there is no stampede of state updates.
   */
  const forgetSession = useCallback(() => {
    api.clearAccessToken();
    setUserState(null);
    setOrganizations([]);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    api.onAuthLost(forgetSession);
  }, [forgetSession]);

  /** Loads the caller's organisations. Failure is not fatal — see below. */
  const loadOrganizations = useCallback(async (): Promise<void> => {
    try {
      setOrganizations(await endpoints.myOrganizations());
    } catch {
      // A failure here is not a reason to sign the user out. They are
      // authenticated; the organisation list is a convenience. An empty list
      // renders the "you are not a member of anything yet" state, which is
      // recoverable, whereas a forced sign-out is not.
      setOrganizations([]);
    }
  }, []);

  /** Adopts a session returned by register, login or refresh. */
  const adopt = useCallback(
    async (session: AuthSession): Promise<void> => {
      api.adoptAccessToken(session.accessToken);
      setUserState(session.user);
      setStatus('authenticated');
      await loadOrganizations();
    },
    [loadOrganizations],
  );

  // The silent restore, run once. `cancelled` guards against a state update if
  // the provider unmounts mid-flight, which happens under React StrictMode's
  // deliberate double-invocation in development.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const refreshed = await api.refresh();
      if (cancelled) return;

      if (!refreshed) {
        setStatus('anonymous');
        return;
      }

      try {
        const me = await endpoints.me();
        if (cancelled) return;
        setUserState(me);
        setStatus('authenticated');
        await loadOrganizations();
      } catch {
        if (cancelled) return;
        // The refresh succeeded but the profile read did not. That is a server
        // problem, not an authentication one — but there is no usable session
        // without a user object, so the honest outcome is anonymous.
        forgetSession();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadOrganizations, forgetSession]);

  const signIn = useCallback<AuthContextValue['signIn']>(
    async (email, password) => {
      try {
        const session = await endpoints.login({ email, password });
        await adopt(session);
        return { ok: true, data: undefined };
      } catch (error) {
        return { ok: false, error: asProblem(error) };
      }
    },
    [adopt],
  );

  const signUp = useCallback<AuthContextValue['signUp']>(
    async (input) => {
      try {
        const session = await endpoints.register(input);
        await adopt(session);
        return { ok: true, data: undefined };
      } catch (error) {
        return { ok: false, error: asProblem(error) };
      }
    },
    [adopt],
  );

  /**
   * Signs out of this device.
   *
   * The local state is cleared **before** the request, and the request failure is
   * swallowed. A sign-out that could fail is a sign-out that leaves someone
   * signed in on a shared machine, so the local clear is unconditional and the
   * server call is best-effort — the session row will expire on its own.
   */
  const signOut = useCallback(async (): Promise<void> => {
    forgetSession();
    try {
      await endpoints.logout();
    } catch {
      // Best effort. The refresh cookie is cleared server-side on success; on
      // failure the token remains valid until it expires, which is a documented
      // trade-off rather than an oversight.
    }
  }, [forgetSession]);

  const signOutEverywhere = useCallback<AuthContextValue['signOutEverywhere']>(async () => {
    try {
      const result = await endpoints.logoutAll();
      forgetSession();
      return { ok: true, data: result.revoked };
    } catch (error) {
      const problem: ApiProblem = asProblem(error);
      // A 401 here means the session was already gone, which is the outcome the
      // caller wanted. Reporting it as a failure would be pedantic.
      if (problem.isAuthFailure) {
        forgetSession();
        return { ok: true, data: 0 };
      }
      return { ok: false, error: problem };
    }
  }, [forgetSession]);

  const organizationFor = useCallback<AuthContextValue['organizationFor']>(
    (slug) => (slug === undefined ? null : (organizations.find((org) => org.slug === slug) ?? null)),
    [organizations],
  );

  const setUser = useCallback((next: UserDTO) => setUserState(next), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      organizations,
      organizationFor,
      signIn,
      signUp,
      signOut,
      signOutEverywhere,
      reloadOrganizations: loadOrganizations,
      setUser,
    }),
    [
      status,
      user,
      organizations,
      organizationFor,
      signIn,
      signUp,
      signOut,
      signOutEverywhere,
      loadOrganizations,
      setUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    // A programming error, not a runtime condition. Throwing with the fix in the
    // message is more useful than a downstream "cannot read property of null".
    throw new Error('useAuth was called outside <AuthProvider>. Wrap the route tree in App.tsx.');
  }
  return context;
}

/**
 * The caller's role in the current organisation, or null.
 *
 * Used by pages to hide controls the caller cannot use. This is a **rendering
 * convenience and never a security boundary** — the API re-checks every
 * permission on every request, and a hidden button that a determined caller
 * re-enables by editing the DOM gets a 403. Hiding it exists so that a guest does
 * not spend a minute filling in a form they cannot submit.
 */
export function useRole(slug: string | undefined): string | null {
  const { organizationFor } = useAuth();
  return organizationFor(slug)?.role ?? null;
}
